import React, { useState } from "react";
// NAVIGATION
import { useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Button, Collapse } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { updateBankToRestore, updateOpenModalRestoreBank } from "../../../redux/banks/bankSlice";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// CUSTOM HOOKS
import useDeletedBanks from "../../../customHooks/GET_requests/useDeletedBanks";
// COMPONENTS
import DeletedBanksTable from "./DeletedBanksTable";
// UTILS
import { DeletedBankRow } from "../../../utils/deletedBanks";
// INTERFACES
import { RootState } from "../../../app/store";

const SECTION_KEY = "deleted-banks";

/**
 * The Banks page's "Deleted banks" section: the banks that were deleted from
 * the organization now selected, each with a Restore action that opens
 * `BanksRestoreModal`.
 *
 * It asks for the same scope `BanksTable` lists: the selected organization, and
 * the server too when the page was opened for one (`?idServer=`). It appears
 * for a user who holds `banks-deleted-view`, offers Restore with `banks-restore`,
 * and requests nothing until expanded.
 */
function BanksDeletedSection() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const location = useLocation();
  const idServer = new URLSearchParams(location.search).get("idServer");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [expanded, setExpanded] = useState(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { globalOrganizationId } = useSelector((state: RootState) => state.home);
  const canView = hasPermission(Permission.BanksDeletedView, permissionSet ?? []);
  const canRestore = hasPermission(Permission.BanksRestore, permissionSet ?? []);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const { rows, total, loaded, loading, error, page, setPage } = useDeletedBanks(
    {
      orgId: globalOrganizationId,
      serverId: idServer && idServer !== "" ? idServer : undefined,
    },
    expanded && canView && Boolean(globalOrganizationId),
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleOpenRestore = (row: DeletedBankRow) => {
    dispatch(updateBankToRestore(row));
    dispatch(updateOpenModalRestoreBank(true));
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  if (!canView || !globalOrganizationId) return null;

  const title = t("edgeManagement.banks.deleted.title");
  return (
    <div className="mt-3">
      <Collapse
        activeKey={expanded ? [SECTION_KEY] : []}
        onChange={(keys) => setExpanded(Array.isArray(keys) ? keys.length > 0 : Boolean(keys))}
      >
        <Collapse.Panel key={SECTION_KEY} header={loaded ? `${title} (${total})` : title}>
          <DeletedBanksTable
            rows={rows}
            total={total}
            page={page}
            loading={loading}
            error={error}
            onPageChange={setPage}
            renderAction={
              canRestore
                ? (row) => (
                    <Button type="link" size="small" onClick={() => handleOpenRestore(row)}>
                      {t("edgeManagement.banks.deleted.restore")}
                    </Button>
                  )
                : undefined
            }
          />
        </Collapse.Panel>
      </Collapse>
    </div>
  );
}

export default BanksDeletedSection;
